
import { defineComponent, ref, reactive, onMounted, onUnmounted } from "vue";
import PianoKey from "./PianoKey.vue";
import VolumeSlider from "./VolumeSlider.vue";
import FrequencySlider from "./FrequencySlider.vue";

export default defineComponent({
  name: "PianoKeyboard",
  components: {
    PianoKey,
    VolumeSlider,
  },
  setup() {
    const notes = [
      { label: "C", frequency: 261.63, type: "white" },
      { label: "C#", frequency: 277.18, type: "black" },
      { label: "D", frequency: 293.66, type: "white" },
      { label: "D#", frequency: 311.13, type: "black" },
      { label: "E", frequency: 329.63, type: "white" },
      { label: "F", frequency: 349.23, type: "white" },
      { label: "F#", frequency: 369.99, type: "black" },
      { label: "G", frequency: 392.0, type: "white" },
      { label: "G#", frequency: 415.3, type: "black" },
      { label: "A", frequency: 440.0, type: "white" },
      { label: "A#", frequency: 466.16, type: "black" },
      { label: "B", frequency: 493.88, type: "white" },
    ];
    const keyMap: { [key: string]: string | undefined } = {
      a: "C",
      w: "C#",
      s: "D",
      e: "D#",
      d: "E",
      f: "F",
      t: "F#",
      g: "G",
      y: "G#",
      h: "A",
      u: "A#",
      j: "B",
    };

    const activeKeys = reactive(
      Object.keys(keyMap).reduce((acc, key) => {
        acc[keyMap[key] as string] = false;
        return acc;
      }, {} as Record<string, boolean>)
    );

    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const noteLabel = keyMap[key];
      if (noteLabel) {
        const noteData = notes.find((n) => n.label === noteLabel);
        if (noteData) {
          playNote(noteData.frequency, noteData.label);
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const noteLabel = keyMap[key];
      if (noteLabel) {
        stopNote(noteLabel);
      }
    };

    onMounted(() => {
      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("keyup", handleKeyUp);
    });

    onUnmounted(() => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    });

    type Synth = {
      audioContext: AudioContext;
      oscillators: Record<string, OscillatorNode>;
      contextResumed: boolean;
      gainNode: GainNode | null;
      waveformType: "sine" | "square" | "triangle" | "sawtooth";
    };

    const synth: Synth = reactive({
      audioContext: new (window.AudioContext || window.webkitAudioContext)(),
      oscillators: {},
      contextResumed: false,
      gainNode: null,
      waveformType: "sine",
    });

    const currentNote = ref("");
    const volume = ref(0.5);

    const ensureAudioContextResumed = async () => {
      if (synth.audioContext.state === "suspended") {
        await synth.audioContext.resume();
        synth.gainNode = synth.audioContext.createGain();
        synth.gainNode.connect(synth.audioContext.destination);
        synth.gainNode.gain.value = volume.value;
      }
    };

    const resumeAudioContext = () => {
      if (!synth.contextResumed) {
        synth.audioContext.resume().then(() => {
          synth.contextResumed = true;
        });
      }
    };

    const playNote = async (frequency: number, label: string) => {
      await ensureAudioContextResumed();
      const noteData = notes.find((n) => n.label === label);
      if (noteData && !synth.oscillators[label]) {
        currentNote.value = noteData.label;
        const oscillator = synth.audioContext.createOscillator();
        oscillator.type = synth.waveformType;
        oscillator.frequency.value = frequency;
        if (synth.gainNode) {
          oscillator.connect(synth.gainNode);
        }
        oscillator.start();
        synth.oscillators[label] = oscillator;
        activeKeys[label] = true;
      }
    };

    const stopNote = (label: string) => {
      if (synth.oscillators[label]) {
        synth.oscillators[label].stop();
        synth.oscillators[label].disconnect();
        delete synth.oscillators[label];
      }
      activeKeys[label] = false;
    };

    const updateVolume = (newVolume: number) => {
      volume.value = newVolume;
    };

    const emitVolumeChange = (newVolume: number) => {
      volume.value = newVolume;
      if (synth.gainNode) {
        synth.gainNode.gain.value = newVolume;
      }
    };

    const getFrequencyForLabel = (label: string) => {
      const noteData = notes.find((n) => n.label === label);
      return noteData ? noteData.frequency : "N/A";
    };

    const waveforms = ["sine", "square", "triangle", "sawtooth"];

    const setWaveformType = (
      type: "sine" | "square" | "triangle" | "sawtooth"
    ) => {
      synth.waveformType = type;
    };

    return {
      synth,
      notes,
      currentNote,
      playNote,
      stopNote,
      volume,
      updateVolume,
      emitVolumeChange,
      resumeAudioContext,
      setWaveformType,
      waveforms,
      getFrequencyForLabel,
      activeKeys,
    };
  },
});
